/**
 * O21 — die EINE Stelle, die entscheidet, was eine MOQ-Warnung sagt.
 *
 * 🔴 WARUM ES DIESE DATEI GIBT
 *
 * Drei Flaechen zeigen dieselbe Zahl: die Mobilkarten und die Tabelle in
 * `PODetail.jsx` (zwei getrennte `items.map`-Schleifen) und der
 * Empfehlungs-Drawer. Zwei Planfassungen hintereinander haben je EINE davon
 * bedient — die erste traf eine Datei ohne Verbraucher, die zweite eine halbe
 * Flaeche. **Der Fehler war beide Male nicht die Adresse, sondern die Annahme,
 * dass es eine gibt.**
 *
 * `utils/mindestpreisWarnung.js` (EK-A15) haelt denselben Vorfall fest, eine
 * Groesse weiter: vier Flaechen, vier Kopien derselben Bedingungen, und drei
 * Gate-Runden fanden dreimal denselben Fehler in je einer ANDEREN Kopie.
 * **Wer vier Kopien pflegt, repariert immer die, fuer die er gerade eine Probe
 * hat.** Die Entscheidung wohnt deshalb hier, einmal.
 *
 * ⚠️ Was hier NICHT wohnt: der Wortlaut. Die drei Flaechen sprechen verschieden
 * ("MOQ: 6", "MOQ 6"), und das soll so bleiben. Geteilt wird, WAS gilt, nicht
 * WIE es klingt.
 *
 * ## Die Herkunft entscheidet, nicht der Status
 *
 * Traegt die Bestellzeile eine eigene `moq`, ist sie der bessere Beleg: es ist
 * die Mindestmenge, gegen die DAMALS bestellt wurde. Sie gilt bei jeder
 * Bestellung, nicht nur bei abgeschlossenen — und sie braucht die Stammdaten
 * nicht, weder geladen noch heil. Deshalb steht sie in `moqLage` an erster
 * Stelle und springt an allen Stammdaten-Schritten vorbei.
 *
 * Eine fruehere Planfassung stellte den Ladezustand nach vorn; eine spaetere
 * den FEHLER-Zustand. Beide haetten die 112 von 229 Zeilen verschluckt, die
 * ihre MOQ selbst tragen. Die Reihenfolge unten ist die Lehre daraus.
 *
 * ## Die Vorrangfrage ist NICHT hier entschieden
 *
 * 🔴 `shp_products."MOQ"` und `vendor_articles.moq` gibt es beide;
 * `backend/models.py:2932` warnt ausdruecklich, dass "ein dritter Ort ohne
 * Vorrangregel drei Wahrheiten" sind, und `backend/vendors.py:2431` haelt fest:
 * "Diese Funktion legt KEINEN Vorrang fest."
 *
 * Dieses Modul liest deshalb **nur, was sein Aufrufer ihm gibt** — heute die
 * Zeilenkopie und `shp_products."MOQ"`. Ein Vorrang, den kein Code umsetzt,
 * gehoert nicht als Behauptung in einen Kommentar, sondern als offene
 * Entscheidung in `docs/projects/einkauf-logistik/ENTSCHEIDE-OFFEN.md` §O21.
 */

export const MOQ_LAEDT          = 'laedt';
export const MOQ_NICHT_LADBAR   = 'nicht_ladbar';
export const MOQ_UNBEKANNT      = 'unbekannt';
export const MOQ_OHNE           = 'ohne';
export const MOQ_MENGE_UNKLAR   = 'menge_unklar';
export const MOQ_ERFUELLT       = 'erfuellt';
export const MOQ_UNTERSCHRITTEN = 'unterschritten';

export const HERKUNFT_ZEILE      = 'zeile';
export const HERKUNFT_STAMMDATEN = 'stammdaten';

/** `''`, `null` und `undefined` sind KEINE Null.
 *
 * 🔴 Auf dem Hauptweg kommt `moq` ungenormt aus dem JSONB: `applyPOData`
 * (`PODetail.jsx:1608`) normiert ausdruecklich nur `cost_per_item`, `tax_pct`
 * und `quantity`. `moq` kann eine Zahl, `null`, `""` — oder eine
 * ZEICHENKETTE sein: `shp_products."MOQ"` ist `db.Numeric`, und `jsonify`
 * einer Numeric-Spalte liefert je nach Fassung `"6.00"`.
 *
 * Deshalb entscheidet diese Funktion, ob eine MOQ da ist, und nicht die
 * Wahrheitswertung `it.moq &&` — die haelt `"0.00"` fuer wahr und `0` fuer
 * falsch, also genau verkehrt herum. */
const zahl = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Ist das eine brauchbare Mindestmenge? `1` ist es NICHT.
 *
 * 🔴 `products_v2.py:223/340` und `UnifiedCreateWizard.jsx:542/627/1525` setzen
 * `moq` per VORGABE auf 1. Eine MOQ von 1 ist keine Mindestmenge, sondern ein
 * fehlender Wert, der wie eine Erlaubnis aussieht — dieselbe Wache, die A15
 * fuer `mindestpreis = 0` zieht. */
const echteMoq = (v) => {
  const n = zahl(v);
  return n !== null && n > 1 ? n : null;
};

/**
 * Was gilt fuer diese Zeile? Die einzige Stelle, die das entscheidet.
 *
 * @param zeilenMoq   die MOQ AUS DER BESTELLZEILE (`it.moq`), roh. Gewinnt.
 * @param stammMoq    die MOQ aus den Stammdaten (`produkt.moq`), roh.
 * @param menge       die Bestellmenge der Zeile, roh.
 * @param produktLage 'laedt' | 'da' | 'fehler' — der Zustand des
 *   Stammdaten-Abrufs. 🔴 NICHT aus `products.length === 0` ableiten: das
 *   heisst gleichzeitig "laedt", "leer" und "gescheitert", weil beide Abrufe
 *   nur `.catch(console.error)` haben.
 * @param produktGefunden  ob das Produkt in den Stammdaten aufloeste.
 *   🔴 Nicht `!!produkt` bei einem `|| {}`-Rueckfall — der macht
 *   "nicht gefunden" von "gefunden, leer" ununterscheidbar.
 *
 * @returns {{art: string, moq: number|null, fehlmenge: number|null, herkunft: string|null}}
 *   🔴 Nur, was auch gelesen wird. A15 reichte vier Felder mit heraus, die
 *   keine Flaeche je las — eine Mutationsprobe blieb ueber 44 Proben gruen.
 *   Ein Feld ohne Verbraucher ist eine stille Zusicherung.
 */
export function moqLage({ zeilenMoq, stammMoq, menge, produktLage, produktGefunden }) {
  const ausZeile = echteMoq(zeilenMoq);

  // 1. Die Zeile weiss selbst Bescheid. Sie braucht die Stammdaten nicht —
  //    weder geladen noch heil. Siehe Kopf, "Die Herkunft entscheidet".
  if (ausZeile !== null) return urteil(ausZeile, menge, HERKUNFT_ZEILE);

  // 2.-4. Ohne Zeilenkopie haengt alles am Stammdaten-Abruf.
  if (produktLage === 'fehler') return leer(MOQ_NICHT_LADBAR);
  if (produktLage !== 'da')     return leer(MOQ_LAEDT);
  if (!produktGefunden)         return leer(MOQ_UNBEKANNT);

  // 5. Produkt da, aber keine echte Mindestmenge hinterlegt.
  const ausStamm = echteMoq(stammMoq);
  if (ausStamm === null) return leer(MOQ_OHNE);

  return urteil(ausStamm, menge, HERKUNFT_STAMMDATEN);
}

const leer = (art) => ({ art, moq: null, fehlmenge: null, herkunft: null });

function urteil(moq, rohMenge, herkunft) {
  const m = zahl(rohMenge);

  // 6. Eine unlesbare MENGE ist keine erfuellte — und sie heisst nicht
  //    "MOQ unbekannt". Zwei Ursachen unter einem Etikett waeren genau der
  //    Einsturz, gegen den dieses Modul gebaut ist.
  //    ⚠️ An der heutigen Flaeche unerreichbar: `quantity` wird an jedem
  //    Einstieg mit `|| 0` normiert. Der Zweig steht trotzdem hier, damit er
  //    richtig besetzt ist, falls die Normierung je faellt.
  if (m === null || m < 0) return { art: MOQ_MENGE_UNKLAR, moq, fehlmenge: null, herkunft };

  // 7. 🔴 `<`, nicht `<=`. Bei `menge === moq` ist die Mindestmenge ERREICHT.
  if (m < moq) {
    // Aufgerundet: `shp_products."MOQ"` ist Numeric OHNE CHECK (models.py:81),
    // Bruchzahlen sind moeglich. "2,5 fehlen" bestellt niemand.
    return { art: MOQ_UNTERSCHRITTEN, moq, fehlmenge: Math.ceil(moq - m), herkunft };
  }
  return { art: MOQ_ERFUELLT, moq, fehlmenge: null, herkunft };
}
